import _ = require("underscore");
import ol = require("ol");
import Utils = require("../utils");
import Portal = require("portal-gis");
import Symbology = require("../utils/symbology");
import Base = require("./radio-command");

const toolId = "annotation-tool";
const layerId = toolId + "-layer";

/*
  POINT: 'Point',
  LINE_STRING: 'LineString',
  LINEAR_RING: 'LinearRing',
  POLYGON: 'Polygon',
  MULTI_POINT: 'MultiPoint',
  MULTI_LINE_STRING: 'MultiLineString',
  MULTI_POLYGON: 'MultiPolygon',
  GEOMETRY_COLLECTION: 'GeometryCollection',
  CIRCLE: 'Circle'
*/

class AnnotationTool extends Base<{}> {

    private map: ol.Map;
    private drawType: string;
    private symbology: string;
    private drawTools = <Rhythm.GIS.Dictionary<ol.interaction.Draw>>{};

    static about = `
Creates a drawing tool.

Usage Example:

    <CONTROL disabled="False" id="annotation-tool" mid="app/controls/view" text="Annotations">
    <COMMANDS>
        <COMMAND id="annotation-polygon-tool" mid="app/commands/annotation-tool" text="Draw Polygon" type="action">
        <OPTIONS>
            <Values id="type" value="Polygon" />
            <Values id="toggle" value="true" />
            <Values id="symbology" value="annotations" />
            <Values id="radio-group" value="geo-selection" />
        </OPTIONS>
        </COMMAND>
        <COMMAND id="annotation-polyline-tool" mid="app/commands/annotation-tool" text="Draw Line" type="action">
        <OPTIONS>
            <Values id="type" value="LineString" />
            <Values id="toggle" value="true" />
            <Values id="symbology" value="annotations" />
            <Values id="radio-group" value="geo-selection" />
        </OPTIONS>
        </COMMAND>
        <COMMAND id="annotation-point-tool" mid="app/commands/annotation-tool" text="Draw Point" type="action">
        <OPTIONS>
            <Values id="type" value="Point" />
            <Values id="toggle" value="true" />
            <Values id="symbology" value="annotations" />
            <Values id="radio-group" value="geo-selection" />
        </OPTIONS>
        </COMMAND>
        <COMMAND id="annotation-multipoint-tool" mid="app/commands/annotation-tool" text="Draw Points" type="action">
        <OPTIONS>
            <Values id="type" value="MultiPoint" />
            <Values id="toggle" value="true" />
            <Values id="symbology" value="annotations" />
            <Values id="radio-group" value="geo-selection" />
        </OPTIONS>
        </COMMAND>
        <COMMAND id="annotation-clear-tool" mid="app/commands/annotation-tool" text="Clear Drawings" type="action">
        <OPTIONS>
            <Values id="stop" value="true" />
            <Values id="clear" value="true" />
        </OPTIONS>
        </COMMAND>
    </COMMANDS>
    </CONTROL>

`;

    static options = Utils.defaults({
        "clear": {
            "about": 'clear annotations',
            "default": "false",
        },
        "start": {
            "about": 'activates the drawing tool of the given type',
            "default": "false",
        },
        "stop": {
            "about": 'suspends the active drawing tool',
            "default": "false",
        },
        "toggle": {
            "about": 'activates if deactivate, deactivates if active',
            "default": "true",
        },
        "type": {
            "about": 'type of geometry to draw',
            "domain": "Point|LineString|Polygon|Circle",
            "default": "Point",
        },
    }, Base.options);

    static input = {};

    static output = {};

    execute(args = this.args) {
        this.mediator.mapViewMap(map => {
            this.map = map;
            super.execute(args);
        });
    }

    go() {
        let options = Utils.defaults(this.asOptions(AnnotationTool.options), {
            symbology: this.configuration.id,
        });

        this.drawType = options.type;
        this.symbology = options.symbology;
        this.forceSource();

        if (options.clear === "true") {
            this.clear();
            return;
        }
        !this.active() && this.start();
    }

    stop() {
        this.map.removeInteraction(this.forceDrawTool(this.drawType));
    }

    private forceDrawTool(drawType: string) {
        var tool = this.drawTools[drawType];
        if (!tool) {
            var map = this.map;
            var vector = this.forceSource();

            let dimensions = {
                "Point": 0,
                "LineString": 1,
                "Polygon": 2,
                "Circle": 2
            };

            let feature = <Rhythm.GIS.FeatureQueryResultFeature>{
                geometryType: drawType,
                attributes: {
                    type: drawType,
                    dimensions: dimensions[drawType]
                }
            };

            Utils.extend(feature, {
                symbology: this.symbology
            });

            let style = Symbology.findStyle(this.mediator, feature);

            tool = new ol.interaction.Draw({
                source: vector.getSource(),
                style: style,
                type: drawType // Point, LineString, Polygon, Circle
            });

            tool.on('drawend', (evt: { feature: ol.Feature }) => {
                evt.feature.setStyle(style);
                var rhythmExt = <Rhythm.GIS.Spatial<Rhythm.GIS.Poly>>{
                    geometry: evt.feature.getGeometry(),
                    spatialReference: {
                        wkid: map.getView().getProjection().getCode()
                    }
                };
                this.trigger(toolId, { poly: rhythmExt });
            });

            this.drawTools[drawType] = tool;
        }
        return tool;
    }

    private forceSource() {
        var map = this.map;
        var vector = <ol.layer.Vector>map.getLayers().getArray().filter(l => l.get("id") === layerId)[0];
        var source = vector && vector.getSource();

        let style = new ol.style.Style({
            fill: new ol.style.Fill({
                color: 'rgba(255, 255, 255, 0.2)'
            }),
            stroke: new ol.style.Stroke({
                color: '#ffcc33',
                width: 2
            }),
            image: new ol.style.Circle({
                radius: 7,
                fill: new ol.style.Fill({
                    color: '#ffcc33'
                })
            })
        });

        if (!source) {
            source = new ol.source.Vector();

            vector = new ol.layer.Vector({
                id: layerId,
                source: source,
                style: style
            });

            map.addLayer(vector);
        }

        return vector;
    }

    private get type() {
        return this.drawType; // readonly
    }

    private active() {
        var result = -1 < this.map.getInteractions().getArray().indexOf(this.forceDrawTool(this.drawType));
        return result;
    }

    private start() {
        this.map.addInteraction(this.forceDrawTool(this.drawType));
    }

    private clear() {
        this.forceSource().getSource().clear();
    }

}


export = AnnotationTool;  